import type { LegacyList } from '@mypoz/shared';

import { legacyApi } from '@/lib/legacy-api.js';

/**
 * Raised when `warehouses` answers with anything but a list.
 *
 * Carries the vendor's own code so the console says which failure this was — `HTTP_502`, `TIMEOUT`,
 * `MALFORMED` — while the screen shows a translated line and a retry. The vendor's `message` is
 * Chinese prose from the endpoint's code table, which is useful in a log and wrong in the UI.
 */
export class WarehousesRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(`warehouses failed (${code}): ${message}`);
    this.name = 'WarehousesRequestError';
  }
}

/**
 * The raw rows from `POST /OutterApi/Api/warehouses`.
 *
 * Deliberately stops at raw rows rather than returning `ShipToAddress[]`: the vendor's copy is
 * doubled per language, so which language a row resolves to is a render-time question, and the
 * provider normalises the same fetched rows again when the reader switches language rather than
 * asking the vendor a second time for data that did not change.
 *
 * POST, as the document specifies and as the live endpoint requires — a GET answers 200 with a
 * bare `[]`, which is the silent-empty-screen failure `LegacyApiClient` exists to prevent, and is
 * why the verb lives in the endpoint registry instead of at call sites like this one.
 */
export async function fetchWarehouseRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('warehouses');

  if (!result.ok) throw new WarehousesRequestError(result.code, result.message);

  return result.data.list;
}
